import React from 'react';

// Caterpillar brand palette (shared with the markdown renderer)
export const MD_CAT_YELLOW = '#FFCD11';
export const MD_CAT_BLACK = '#1A1A1A';

// Per-request character budget: text longer than this is split into chunks that
// are analyzed separately and merged back into one result.
export const CHUNK_CHAR_LIMIT = 6000;

// Absolute safety cap on how much epic/pasted text we accept before chunking, so a
// pathologically large description can't fan out into unbounded API calls.
export const MAX_TOTAL_CHARS = 60000;

// Adoption Enabler service base + the localStorage key holding the active chat id.
// Shared across the chat, feedback and UI-mapping calls.
export const PROXY_BASE = `https://adoption-enabler-service.platform.dev.helios-internal.cat.com/heliosAdoptionEnabler`;
export const SESSION_STORAGE_KEY = "adoptionAssistant_sessionId";

// Published Copilot Studio ("AI agent") direct-connect endpoint + token scope.
export const AGENT_DIRECT_CONNECT_URL =
  'https://25e959f83996e574a66c89027275e2.08.environment.api.powerplatform.com/copilotstudio/dataverse-backed/authenticated/bots/cr1d7_CatalogChatBot/conversations?api-version=2022-03-01-preview';
export const AGENT_SCOPE = 'https://api.powerplatform.com/.default';

// Leaves agent mode on an explicit exit command.
export const AGENT_EXIT = /^\s*(?:exit|\/exit|quit|exit\s+(?:the\s+)?(?:ai\s+)?agent|leave\s+(?:the\s+)?(?:ai\s+)?agent|stop\s+agent|end\s+chat|back\s+to\s+assistant)\s*$/i;

// Clean quick-action labels (shown as chat options) mapped to the phrasing the
// Copilot Studio agent understands for its two skills.
export const AGENT_QUICK_ACTIONS: Record<string, string> = {
  'raise an adoption request': 'I want to raise a new adoption request',
  'track my adoption request': 'I want to track the status of my adoption request',
};

// Rotating status lines shown in the typing indicator while a slow agent turn runs
// (the agent streams no progress during long SharePoint/entitlement lookups, so the
// indicator would otherwise freeze on "Processing").
export const AGENT_PROGRESS_STEPS = [
  'Thinking…',
  'Processing your request…',
  'Checking the SharePoint ID…',
  'Validating the SharePoint ID…',
  'Fetching the adoption request…',
  'Fetching endpoints…',
  'Getting application names from entitlements…',
  'Getting party numbers…',
  'Checking access details…',
  'Gathering the information…',
  'Almost there…',
];

export const ADAPTIVE_CARD_CONTENT_TYPE = 'application/vnd.microsoft.card.adaptive';

// Coverage value -> badge colors (GAP red, Partial yellow/amber, Covered green,
// Under Review neutral blue for freshly added-but-unverified capabilities).
export const COVERAGE_STYLES: Record<string, React.CSSProperties> = {
  gap: { backgroundColor: '#fdecea', color: '#c62828', border: '1px solid #f3b9b4' },
  partial: { backgroundColor: '#fff8e1', color: '#b8860b', border: '1px solid #f2d385' },
  covered: { backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a9d9ac' },
  'under review': { backgroundColor: '#e8eef7', color: '#1565c0', border: '1px solid #b7cdea' },
};

// Coverage label used when a capability is added to the table via the check-api
// flow — it stays "Under Review" until a human verifies the match.
export const UNDER_REVIEW_COVERAGE = 'under review';

// Sentinel payload telling the "action" block to patch the table locally (no API call).
export const LOCAL_UPDATE_TABLE = "__local_update_table__";

// localStorage keys for the per-session capability image + notes stores.
export const CAP_IMAGE_STORAGE_KEY = 'adoptionAssistant_capabilityImages';
export const CAP_NOTES_STORAGE_KEY = 'adoptionAssistant_capabilityNotes';
